import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Shield, Check, Clock, Monitor, MapPin, Download } from 'lucide-react';

import API_BASE_URL from '../config/api';
const API_URL = API_BASE_URL;

const ConsentModal = ({ patientId, patientName, onClose }) => {
  const [consent, setConsent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchConsent();
  }, [patientId]);

  const fetchConsent = async () => {
    try {
      const response = await axios.get(`${API_URL}/patients/${patientId}/consent`);
      // Handle both array response and object with latest
      const consentData = Array.isArray(response.data) ? response.data[0] : response.data?.latest;
      setConsent(consentData || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load consent');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API_URL}/patients/${patientId}/consent-pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Consent_${patientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download consent PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading consent record...</p>
        </div>
      </div>
    );
  }

  if (error || !consent) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h3 className="text-xl font-semibold mb-2">No Consent Record Found</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
          <div className="flex justify-between items-start">
            <div className="flex items-center">
              <Shield className="w-8 h-8 mr-3" />
              <div>
                <h2 className="text-2xl font-bold">Consent Record</h2>
                <p className="text-blue-100 mt-1">{patientName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Signature Section */}
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <Check className="w-6 h-6 text-green-600 mr-2" />
              <h3 className="text-lg font-semibold text-green-900">Electronic Signature</h3>
            </div>
            <div className="bg-white border-2 border-green-300 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-gray-900 italic">
                {consent.electronicSignature}
              </p>
            </div>
          </div>

          {/* Timestamp & Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center text-gray-700 mb-2">
                <Clock className="w-5 h-5 mr-2" />
                <span className="font-semibold">Signed On</span>
              </div>
              <p className="text-gray-900 ml-7">
                {new Date(consent.timestamp).toLocaleString('en-NZ', {
                  dateStyle: 'full',
                  timeStyle: 'long'
                })}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center text-gray-700 mb-2">
                <MapPin className="w-5 h-5 mr-2" />
                <span className="font-semibold">IP Address</span>
              </div>
              <p className="text-gray-900 ml-7 font-mono text-sm">
                {consent.ipAddress}
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center text-gray-700 mb-2">
              <Monitor className="w-5 h-5 mr-2" />
              <span className="font-semibold">Device Information</span>
            </div>
            <p className="text-gray-900 ml-7 text-sm break-all">
              {consent.userAgent}
            </p>
          </div>

          {/* Consent Items */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Consent Given For:</h3>
            <div className="space-y-3">
              <ConsentItem
                label="Treatment Consent"
                description="Consent to assessment and treatment by the healthcare provider"
                checked={consent.consentTreatment}
              />
              <ConsentItem
                label="Privacy & Information Collection"
                description="Consent to collection, storage, and use of personal and health information"
                checked={consent.consentPrivacy}
              />
              <ConsentItem
                label="Information Sharing (ACC, GP, Healthcare)"
                description="Consent to share information with ACC, GP, and other healthcare providers"
                checked={consent.consentInformationSharing}
              />
              <ConsentItem
                label="Telehealth Consultation"
                description="Consent to telehealth appointments where clinically appropriate"
                checked={consent.consentTelehealth}
              />
              <ConsentItem
                label="Audio Recording"
                description="Consent to consultation being audio recorded for preparing consultation notes"
                checked={consent.consentAudioRecording}
              />
              <ConsentItem
                label="Payment Agreement"
                description="Agreement to payment terms and conditions"
                checked={consent.consentPayment}
              />
              <ConsentItem
                label="Use of AI Technology"
                description="Acknowledgement that AI technology (Claude by Anthropic) is used to process health information for clinical care purposes"
                checked={consent.consentAI}
              />
              <ConsentItem
                label="AI Research Data Contribution (Optional)"
                description="Consent for anonymised health information to be used for improving medical AI diagnostic and management models"
                checked={consent.consentAITraining}
              />
            </div>
          </div>

          {/* Legal Framework */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Legal Framework</h4>
            <p className="text-xs text-blue-800">
              This consent is obtained in accordance with the Health and Disability Commissioner 
              (Code of Health and Disability Services Consumers' Rights) Regulations 1996, 
              Privacy Act 2020, Health Information Privacy Code 2020, and Accident Compensation Act 2001.
            </p>
          </div>

          {/* Record IDs (for admin) */}
          <div className="bg-gray-100 rounded-lg p-4">
            <p className="text-xs text-gray-600">
              <strong>Consent ID:</strong> <span className="font-mono">{consent.id}</span>
            </p>
            <p className="text-xs text-gray-600 mt-1">
              <strong>Patient ID:</strong> <span className="font-mono">{consent.patientId}</span>
            </p>
            <p className="text-xs text-gray-600 mt-1">
              <strong>Form ID:</strong> <span className="font-mono">{consent.formId}</span>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-between">
          <button
            onClick={downloadPDF}
            disabled={downloading}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center disabled:bg-gray-400"
          >
            <Download className="w-5 h-5 mr-2" />
            {downloading ? 'Downloading...' : 'Download PDF'}
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper component for consent items
const ConsentItem = ({ label, description, checked }) => (
  <div className={`flex items-start p-3 rounded-lg border-2 ${
    checked 
      ? 'bg-green-50 border-green-300' 
      : 'bg-gray-50 border-gray-200'
  }`}>
    {checked ? (
      <Check className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
    ) : (
      <X className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0 mt-0.5" />
    )}
    <div>
      <span className={`font-medium ${checked ? 'text-green-900' : 'text-gray-500'}`}>
        {label}
      </span>
      {description && (
        <p className={`text-xs mt-1 ${checked ? 'text-green-700' : 'text-gray-400'}`}>
          {description}
        </p>
      )}
    </div>
  </div>
);

export default ConsentModal;
